/**
 * "New booking" for the person on the call (call-booking B1): one screen, opened from the in-call bar, the talking banner
 * and any row of Recent calls. It starts from the caller and the call's note, and makes the booking the public form would
 * make — the party, the dates, a room type per room, a name for everyone, special requests, the organizer's questions —
 * with a staff note and how they heard about the event. Every figure is the server's (quote_registration). The guest pays
 * by card on the call (the provider's own fields; only a token leaves the browser) or by the link in the confirmation
 * email, which then carries the balance. The booking is confirmed at once, and its number goes onto the call's note.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, SelectField, TextArea, TextField } from '@/components/ui/Field';
import { InfoTip } from '@/components/ui/InfoTip';
import { InlineReport } from '@/components/ui/ReportControl';
import { CardForm, type CardFormHandle } from '@/features/public/CardForm';
import { bookingOptions, getPublicEvent, hotelCardsOf, type PublicEvent } from '@/features/public/api';
import { consentGiven, consentIds, consentsOf } from '@/features/booking/consent';
import { GENDER_CHOICES, genderAnswer, genderAsk } from '@/features/booking/gender';
import { EMAIL_RE, PHONE_RE, customKey, personOf, requestsOf, slotsOf, withPerson, type Slot } from '@/features/booking/people';
import { useBookingPrice } from '@/features/booking/price';
import { capsOf, hotelRooms, listedHotels, overPlaced, peopleLine, pickRooms, placeAll, poolOf, typeTag, viewRoom, bookingBase, hotelOf } from '@/features/booking/rooms';
import { SPECIAL_REQUESTS_MAX, freshDraft, limitsOf, mostRooms, peopleOf, soldOf, stayOf, stayRulesOf, whoMessage, whoProblem, withGroupCount, withRooms, type Draft, type DraftPerson, type DraftRoom } from '@/features/booking/state';
import { arriveChoices, leaveChoices } from '@/features/booking/stayDates';
import { bodyOf, guestOf, replayKeyFor, type HeldKey } from '@/features/booking/submit';
import { summaryRows } from '@/features/booking/Summary';
import { aliasFailure } from '@/lib/failures';
import { cn } from '@/lib/cn';
import { date, dateRange, dateTime, money, phone } from '@/lib/format';
import {
  HOLD_DEFAULT_HOURS, HOLD_MAX_HOURS, NOTE_LOAD_FAILED, callNote, fetchHoldDefault, otherParty, saveCallNote, staffBook, toE164,
  type CallNoteRow, type NoteCall, type StaffBooked, type StaffHold, type StaffMail, type StaffPay,
} from './data';

export const BOOK_EVENT_CLOSED = 'This event can’t be booked from here right now.';
export const BOOK_EVENT_FAILED = 'Couldn’t load this event’s booking details.';
export const ROOMS_SHORT = 'Everyone needs a room: add a room or choose a bigger type.';
export const NAMES_MISSING = 'Add a first and last name for everyone.';
export const CARD_NOT_SET_UP = 'Card payments aren’t set up for this event. Choose Email pay link.';
export const NOTE_NOT_SAVED = 'Note not saved, booking is.';
export const HOLD_HOURS_BAD = `Enter whole hours from 1 to ${HOLD_MAX_HOURS}.`;
const MAIL_LINE: Record<StaffMail, string> = {
  sent: 'Confirmation emailed.',
  queued: 'Confirmation queued — it goes out within a minute.',
  // email always has a sender (the no-reply address at least): what is left 'no-provider' is a test address in practice
  'no-provider': 'No confirmation was emailed: this is a test address, or the server has no email sender.',
  failed: 'The confirmation email could not be sent.',
};
/** The server refused because the price moved or a type filled: ask for the price again. */
const REPRICE = new Set(['total_mismatch', 'price_changed', 'payment_required', 'type_full', 'room_unavailable', 'type_closed']);

const text = (v: string | undefined) => (v ?? '').trim();
const shown = (n: string | null | undefined) => (n ? (toE164(n) ? phone(n) : n) : '');
/** "Sara Bat Levi" → first "Sara Bat", last "Levi": the name is split on its last space. */
export function splitName(name: string | null | undefined): { first_name: string; last_name: string } {
  const t = (name ?? '').trim().replace(/\s+/g, ' ');
  const at = t.lastIndexOf(' ');
  return at < 0 ? { first_name: t, last_name: '' } : { first_name: t.slice(0, at), last_name: t.slice(at + 1) };
}
/** The guest's details as the call knows them: the note's name, phone and email, else the number on the call. */
export function prefillOf(call: NoteCall | null, note: CallNoteRow | null): Record<string, string> {
  const other = call ? otherParty(call) : null;
  return { ...splitName(note?.name), phone: shown(note?.phone ?? toE164(other ?? '') ?? other), email: note?.email ?? '' };
}

export function BookingDialog({ callId, open, onClose }: { callId: string | null; open: boolean; onClose(): void }) {
  const q = useQuery({ queryKey: ['call-note', callId], queryFn: () => callNote(callId!), enabled: open && callId !== null });
  const call = q.data?.call ?? null;
  const eventId = call?.event_id ?? null;
  const ev = useQuery({ queryKey: ['public-event', eventId], queryFn: () => getPublicEvent(eventId!), enabled: open && eventId !== null, staleTime: 30_000, retry: 1 });
  // each open is a new booking: a fresh form and a fresh replay key
  const [round, setRound] = useState(0);
  useEffect(() => { if (open) setRound((n) => n + 1); }, [open, callId]);
  const [busy, setBusy] = useState(false);
  const [footer, setFooter] = useState<HTMLDivElement | null>(null);
  useEffect(() => { if (!open) setBusy(false); }, [open]);

  const loadFailed = q.isError || (q.isSuccess && !call) || ev.isError;
  const event = ev.data ?? null;
  const ready = open && Boolean(call) && Boolean(event) && q.data !== undefined;
  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title="New booking"
      description={call ? `${call.direction === 'in' ? 'Call from' : 'Call to'} ${shown(otherParty(call)) || 'a hidden number'}` : undefined}
      size="xl"
      closeDisabled={busy}
      footer={ready ? <div ref={setFooter} className="contents" /> : undefined}
    >
      <div data-testid="call-book-dialog" className="flex flex-col gap-5">
        {loadFailed ? (
          <div className="flex flex-wrap items-center gap-2">
            <p role="alert" className="text-st-danger" data-testid="call-book-error">{ev.isError ? BOOK_EVENT_FAILED : NOTE_LOAD_FAILED}</p>
            <InlineReport message={ev.isError ? BOOK_EVENT_FAILED : NOTE_LOAD_FAILED} />
          </div>
        ) : ev.isSuccess && !event ? (
          <p role="alert" className="text-st-danger" data-testid="call-book-closed">{BOOK_EVENT_CLOSED}</p>
        ) : !ready ? <p className="text-ink-2">Loading…</p> : (
          <BookingForm key={`${callId}:${round}`} event={event!} call={call!} note={q.data!.note} footer={footer} onBusy={setBusy} onClose={onClose} />
        )}
      </div>
    </Dialog>
  );
}

function Section({ title, children, tip, testId }: { title: string; children: React.ReactNode; tip?: React.ReactNode; testId?: string }) {
  return (
    <section className="flex min-w-0 flex-col gap-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
        {tip && <InfoTip title={title} align="end">{tip}</InfoTip>}
      </div>
      {children}
    </section>
  );
}
function Problem({ line, testId, report = false }: { line: string; testId: string; report?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p role="alert" className="text-sm text-st-danger" data-testid={testId}>{line}</p>
      {report && <InlineReport message={line} />}
    </div>
  );
}

function BookingForm({ event, call, note, footer, onBusy, onClose }: {
  event: PublicEvent; call: NoteCall; note: CallNoteRow | null; footer: HTMLDivElement | null; onBusy(b: boolean): void; onClose(): void;
}) {
  const qc = useQueryClient();
  const rules = useMemo(() => stayRulesOf(event), [event]);
  const { groups, adultRequired } = useMemo(() => peopleOf(event), [event]);
  const limits = useMemo(() => limitsOf(event), [event]);
  const sold = useMemo(() => soldOf(event), [event]);
  const groupOrder = useMemo(() => groups.map((g) => g.key), [groups]);
  const cribKey = groups.find((g) => g.crib)?.key ?? null;
  const ask = genderAsk(event.gender);
  const questions = event.registration_form_fields ?? [];
  const consents = useMemo(() => consentsOf(event), [event]);
  const currency = event.currency || 'USD';

  const [draft, setDraft] = useState<Draft>(() => ({ ...freshDraft(event), details: prefillOf(call, note) }));
  const [ticked, setTicked] = useState<string[]>([]);
  const [staffNote, setStaffNote] = useState('');
  const [heard, setHeard] = useState('');
  const [pay, setPay] = useState<StaffPay>('card');
  /** The hold's hours as typed (null = untouched: the event's default, which the server applies), or no end. */
  const [holdHours, setHoldHours] = useState<string | null>(null);
  const [holdNever, setHoldNever] = useState(false);
  const [tried, setTried] = useState(false);
  const [line, setLine] = useState<{ text: string; report: boolean } | null>(null);
  /** A card was charged and what became of it is not known: nothing more is sent from this screen. */
  const [locked, setLocked] = useState<string | null>(null);
  const [done, setDone] = useState<StaffBooked | null>(null);
  const [noteLine, setNoteLine] = useState<string | null>(null);
  const [busy, setBusyState] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const card = useRef<CardFormHandle>(null);
  const held = useRef<HeldKey | null>(null);
  const sending = useRef(false);
  const setBusy = (b: boolean) => { setBusyState(b); onBusy(b); };

  const details = draft.details ?? {};
  const change = (d: Draft) => { setDraft(d); setLine(null); };
  const set = (key: string, value: string) => change({ ...draft, details: { ...details, [key]: value } });
  /** The party, the nights or the hotel changed: the rooms are picked again. */
  const reparty = (d: Draft) => change({ ...d, rooms: undefined });

  // the dates
  const stay = stayOf(rules, draft);
  const arrivals = useMemo(() => arriveChoices(rules, sold), [rules, sold]);
  const leaves = useMemo(() => (draft.arrive ? leaveChoices(rules, draft.arrive, sold) : []), [rules, draft.arrive, sold]);
  const who = whoProblem(rules, groups, draft, sold, adultRequired);

  // the hotel and what it can give this party
  const cards = hotelCardsOf(event);
  const hotel = hotelOf(event, draft.hotelId);
  const base = bookingBase(event, rules, draft, stay, hotel?.id ?? null);
  const optionsQ = useQuery({
    queryKey: ['book-options', event.id, JSON.stringify(base), draft.roomsWanted],
    queryFn: () => bookingOptions(event.id, { ...base, rooms_wanted: draft.roomsWanted }),
    enabled: who === null, staleTime: 0, retry: 1, refetchOnWindowFocus: false,
  });
  const options = who === null ? optionsQ.data : undefined;
  const statuses = useMemo(() => options?.types ?? [], [options]);
  const hotels = listedHotels(event, options?.hotels ?? []);
  const optionsRefused = options?.room_error && !(cards.length > 1 && !hotel && options.room_error === 'hotel_required') ? (options.room_message || 'These dates can’t be booked.') : null;
  const ready = Boolean(hotel && options && !options.room_error);
  const h = useMemo(() => hotelRooms(event, hotel, { arrive: stay.arrive, leave: stay.leave }), [event, hotel, stay.arrive, stay.leave]);
  const most = mostRooms(draft, groups, limits);
  const caps = useMemo(() => capsOf(h, statuses, {}, most), [h, statuses, most]);
  const party = draft.groups;
  const rooms = useMemo((): DraftRoom[] => {
    if (!ready) return [];
    if (draft.rooms) return overPlaced(draft.rooms, party) ? placeAll(draft.rooms, h, party, groupOrder, cribKey) : draft.rooms;
    return pickRooms(h, caps, draft.roomsWanted, party, groupOrder, cribKey, limits.rooms)?.rooms ?? [];
  }, [ready, draft.rooms, draft.roomsWanted, h, caps, party, groupOrder, cribKey, limits.rooms]);
  const views = rooms.map((r) => (h.byCode.get(r.type) ? viewRoom(r, h.byCode.get(r.type)!, cribKey) : null));
  const left = Object.values(poolOf(rooms, party)).reduce((a, b) => a + b, 0);
  const roomsOk = ready && rooms.length > 0 && left === 0 && views.every((v) => v?.valid);
  /** One room's type changed: the whole party is spread across the rooms again. */
  const setType = (i: number, type: string) => {
    const next = rooms.map((r, j) => (j === i ? { type, people: r.people } : { type: r.type, people: r.people, ...(r.group ? { group: r.group, ...(r.any ? { any: true } : {}) } : {}) }));
    change({ ...draft, rooms: placeAll(next, h, party, groupOrder, cribKey) });
  };

  // the price, the server's, of exactly these rooms
  const priced = { ...draft, rooms };
  const price = useBookingPrice(event, event.id, priced, roomsOk);
  const quote = price.quote;
  const chargeNow: number = quote && quote.requires_payment && (quote.charge_cents ?? 0) > 0 ? (quote.charge_cents ?? 0) : 0;
  // nothing to charge now: a plain booking (pay 'card' with no card), as the public form makes it; never the pay link, whose
  // "pay it from your stay page" line would send the guest to a page that can't take the payment
  const mode: StaffPay = chargeNow > 0 ? pay : 'card';
  // the event's default hold; unreadable → 24, sent as typed so what is shown is what is held (D10)
  const holdDefaultQ = useQuery({ queryKey: ['hold-default', event.id], queryFn: () => fetchHoldDefault(event.id), enabled: chargeNow > 0, staleTime: 60_000, retry: 1 });
  const holdDefault = holdDefaultQ.data ?? null;
  const holdShown = holdHours ?? String(holdDefault?.hours ?? HOLD_DEFAULT_HOURS);
  const holdN = Number(holdShown);
  const holdBad = mode === 'hold' && !holdNever && !(/^\d+$/.test(holdShown.trim()) && holdN >= 1 && holdN <= HOLD_MAX_HOURS);
  const hold: StaffHold | null = mode !== 'hold' ? null
    : holdNever ? { never: true }
    : holdHours === null && holdDefault?.known ? {}
    : { hours: holdN };
  const cardGateway = event.payment_gateway === 'cardknox' || event.payment_gateway === 'authnet' ? event.payment_gateway : null;
  const cardPath = mode === 'card' && chargeNow > 0 && !event.sandbox;
  const cardUnavailable = cardPath && (!cardGateway || !event.gateway_client_key);
  useEffect(() => { if (!cardPath) setCardReady(false); }, [cardPath]);

  // the people
  const slots = useMemo(() => slotsOf(rooms, groups), [rooms, groups]);
  const setPerson = (slot: Slot, patch: Partial<Pick<DraftPerson, 'first_name' | 'last_name' | 'gender'>>) => {
    if (slot.main && (patch.first_name !== undefined || patch.last_name !== undefined)) {
      const { gender, ...name } = patch;
      change({ ...draft, details: { ...details, ...name }, ...(gender !== undefined ? { names: withPerson(draft.names, slots, slot, { gender }) } : {}) });
    } else change({ ...draft, names: withPerson(draft.names, slots, slot, patch) });
  };
  const nameOf = (s: Slot) => (s.main ? { first_name: details.first_name, last_name: details.last_name } : personOf(draft.names, s));
  const problems = useMemo(() => {
    const out: Record<string, string> = {};
    if (!EMAIL_RE.test(text(details.email))) out.email = 'Enter a valid email.';
    if (!PHONE_RE.test(text(details.phone))) out.phone = 'Enter a valid phone number.';
    if (slots.flat().some((s) => !text(nameOf(s).first_name) || !text(nameOf(s).last_name))) out.names = NAMES_MISSING;
    if (ask === 'required' && slots.flat().some((s) => !genderAnswer(personOf(draft.names, s).gender))) out.gender = 'Choose Male or Female for everyone.';
    for (const qn of questions) if (qn.required && !text(details[customKey(qn.id)])) out[customKey(qn.id)] = `${qn.label || 'This field'} is required.`;
    if (!consentGiven(consents, ticked)) out.consent = 'The guest must agree to each required line.';
    return out;
  }, [details, slots, draft.names, ask, questions, consents, ticked]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownProblems = tried ? problems : {};

  // the booking as it is sent, and its key: the same while nothing changes, a new one after any change
  const guest = useMemo(() => {
    if (!price.request) return null;
    const g = guestOf(event, draft, slots, price.request, null, consents.length ? consentIds(consents, ticked) : null);
    if (!requestsOf(event.form).folded.includes('special_requests')) g.special_requests = text(details.special_requests);
    return g;
  }, [event, draft, slots, price.request, consents, ticked, details.special_requests]);
  const body = guest && quote && typeof quote.total_cents === 'number' ? `${bodyOf(event.id, guest, null, false)}|${mode}|${staffNote.trim()}|${heard.trim()}|${quote.total_cents}|${chargeNow}|${hold ? JSON.stringify(hold) : ''}` : null;
  useEffect(() => { if (body) held.current = replayKeyFor(held.current, body); }, [body]);

  const canSend = !done && !locked && !busy && Boolean(guest && quote && body) && !price.pending && !cardUnavailable && (!cardPath || cardReady)
    && !holdBad && (mode !== 'hold' || holdNever || holdHours !== null || holdDefaultQ.isFetched);
  const submit = async () => {
    if (sending.current || !canSend || !guest || !quote || !body || typeof quote.total_cents !== 'number') return;
    if (Object.keys(problems).length) {
      setTried(true);
      window.setTimeout(() => document.querySelector<HTMLElement>('[data-testid="call-book-dialog"] [aria-invalid="true"], [data-testid="call-book-dialog"] [role="alert"]')?.scrollIntoView({ block: 'center' }), 0);
      return;
    }
    sending.current = true;
    setLine(null);
    setBusy(true);
    try {
      let token: unknown = null;
      if (cardPath) {
        try {
          token = await card.current!.tokenize();
        } catch (e) {
          setLine({ text: (e as Error).message, report: false });
          return;
        }
      }
      const key = replayKeyFor(held.current, body);
      held.current = key;
      const r = await staffBook({
        event_id: event.id, call_id: call.id, idempotency_key: key.key, guest, companions: [], quantities: [], pay: mode, ...(hold ? { hold } : {}),
        card: cardPath ? token : null, client_total_cents: quote.total_cents, expect_charge_cents: mode === 'card' ? chargeNow : 0,
        internal_note: staffNote.trim() || null, heard: heard.trim() || null,
      });
      if (r.ok) {
        setDone(r);
        void qc.invalidateQueries({ queryKey: ['event-calls', event.id] });
        void writeNote(r);
        return;
      }
      if (r.payment === 'unsure' || r.payment === 'not-reversed') { setLocked(r.message); return; }
      setLine({ text: r.message, report: true });
      if (REPRICE.has(r.error)) price.retry();
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };
  /** The booking number goes onto the call's note. A note that will not save leaves the booking as it is. */
  const writeNote = async (r: StaffBooked) => {
    if (!r.confirmation_number) return;
    const n = await saveCallNote({ call_id: call.id, booking_ref: r.confirmation_number.slice(0, 64), reason: 'booking', ...(note ? { version: note.version } : {}) });
    if (!n.ok) {
      const said = `${NOTE_NOT_SAVED} ${n.message}`.trim();
      aliasFailure(n.message, said);
      setNoteLine(said);
      return;
    }
    void qc.invalidateQueries({ queryKey: ['call-note', call.id] });
    void qc.invalidateQueries({ queryKey: ['event-calls', event.id] });
  };

  const label = chargeNow > 0 && mode === 'card' ? `Charge ${money(chargeNow, currency)}` : mode === 'hold' ? 'Hold room' : chargeNow > 0 ? 'Book and email pay link' : 'Book';
  const footerButtons = done ? (
    <Button variant="primary" className="max-sm:min-h-11" onClick={onClose} data-testid="call-book-done">Done</Button>
  ) : (
    <>
      <Button variant="secondary" className="max-sm:min-h-11" disabled={busy} onClick={onClose} data-testid="call-book-cancel">Cancel</Button>
      <Button variant="primary" className="max-sm:min-h-11" disabled={!canSend} busy={busy} busyLabel={cardPath ? 'Charging…' : 'Booking…'} onClick={() => void submit()} data-testid="call-book-submit">{label}</Button>
    </>
  );
  const portal = footer ? createPortal(footerButtons, footer) : null;

  if (done) {
    return (
      <>
        {portal}
        <div className="flex flex-col gap-2" data-testid="call-book-done-panel" aria-live="polite">
          <p className="text-[15px] font-semibold text-ink">Booked{done.confirmation_number ? <> · Confirmation <span data-testid="call-book-confirmation">{done.confirmation_number}</span></> : null}</p>
          <p className="text-sm text-ink-2" data-testid="call-book-payment">
            {done.charged_cents > 0 ? `Charged ${money(done.charged_cents, currency)} to the card.` : 'Nothing was charged.'}
            {done.balance_cents > 0 ? ` Balance ${money(done.balance_cents, currency)}${done.balance_due_date ? `, due ${date(done.balance_due_date)}` : ''}.` : ''}
          </p>
          {(done.hold_expires_at || done.hold_never) && (
            <p className="text-sm text-ink-2" data-testid="call-book-held">{done.hold_never ? 'The room is held with no end date.' : `The room is held until ${dateTime(done.hold_expires_at!)}.`}</p>
          )}
          {done.waitlisted_room_type && <p className="text-sm text-ink-2" data-testid="call-book-waitlisted">That room type was full, so the booking is on its waiting list until a room frees up.</p>}
          <p className="text-sm text-ink-2" data-testid="call-book-mail">{MAIL_LINE[done.mail] ?? MAIL_LINE.failed}</p>
          {noteLine && <Problem line={noteLine} testId="call-book-note-error" report />}
        </div>
      </>
    );
  }

  const summary = price.summary ? summaryRows(price.summary).filter((r) => r.kind !== 'due') : [];
  return (
    <>
      {portal}
      <div className="grid min-w-0 gap-5 lg:grid-cols-2 lg:items-start">
        <div className="flex min-w-0 flex-col gap-5">
          <Section title="Party and dates" testId="call-book-party">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[...groups].reverse().map((g) => (
                <SelectField key={g.key} label={g.name} value={String(draft.groups[g.key] ?? 0)} disabled={busy}
                  onChange={(e) => reparty(withGroupCount(draft, groups, g.key, Number(e.target.value), limits))} data-testid="call-book-group" data-group={g.key}>
                  {Array.from({ length: limits.people + 1 }, (_, n) => <option key={n} value={n}>{n}</option>)}
                </SelectField>
              ))}
              <SelectField label="Rooms" value={String(draft.roomsWanted)} disabled={busy} onChange={(e) => reparty(withRooms(draft, groups, Number(e.target.value), limits))} data-testid="call-book-rooms">
                {Array.from({ length: most }, (_, n) => <option key={n + 1} value={n + 1}>{n + 1}</option>)}
              </SelectField>
            </div>
            {rules.mode === 'fixed' ? (
              <p className="text-sm text-ink-2" data-testid="call-book-dates">{dateRange(stay.arrive, stay.leave)}</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Arrive" value={draft.arrive ?? ''} disabled={busy} data-testid="call-book-arrive"
                  onChange={(e) => { const a = e.target.value; const l = leaveChoices(rules, a, sold); reparty({ ...draft, arrive: a, leave: draft.leave && l.includes(draft.leave) ? draft.leave : (l[0] ?? null) }); }}>
                  {arrivals.map((d) => <option key={d} value={d}>{date(d)}</option>)}
                </SelectField>
                <SelectField label="Leave" value={draft.leave ?? ''} disabled={busy} data-testid="call-book-leave" onChange={(e) => reparty({ ...draft, leave: e.target.value })}>
                  {leaves.map((d) => <option key={d} value={d}>{date(d)}</option>)}
                </SelectField>
              </div>
            )}
            {who && <Problem line={whoMessage(who, rules, groups)} testId="call-book-who" />}
          </Section>

          <Section title="Rooms" testId="call-book-room-list">
            {cards.length > 1 && (
              <SelectField label="Hotel" value={hotel?.id ?? ''} disabled={busy} data-testid="call-book-hotel" onChange={(e) => reparty({ ...draft, hotelId: e.target.value || null })}>
                <option value="">Choose a hotel</option>
                {hotels.map(({ hotel: c, status }) => (
                  <option key={c.id} value={c.id} disabled={status !== 'available'}>{status === 'available' ? c.hotel_name : `${c.hotel_name} — ${status === 'full' ? 'Full' : 'Closed'}`}</option>
                ))}
              </SelectField>
            )}
            {who === null && optionsQ.isError && <Problem line="Couldn’t check the rooms — try again." testId="call-book-options-error" />}
            {optionsRefused && <Problem line={optionsRefused} testId="call-book-options-refused" />}
            {who === null && !options && !optionsQ.isError && <p className="text-sm text-ink-2">Checking rooms…</p>}
            {ready && rooms.length === 0 && <Problem line={ROOMS_SHORT} testId="call-book-rooms-short" />}
            <ol className="flex flex-col gap-3">
              {rooms.map((r, i) => (
                <li key={i} className="min-w-0 rounded-control bg-surface p-3 ring-1 ring-line" data-testid="call-book-room" data-room={i + 1}>
                  <SelectField label={`Room ${i + 1}`} value={r.type} disabled={busy} onChange={(e) => setType(i, e.target.value)} data-testid="call-book-type">
                    {h.types.map((t) => {
                      const s = statuses.find((x) => x.code === t.code)?.status;
                      const tag = t.code === r.type ? null : s === 'not_sold' ? 'Not sold these nights' : typeTag(t, statuses, caps);
                      return <option key={t.code} value={t.code} disabled={tag !== null}>{tag ? `${t.label} — ${tag}` : t.label}</option>;
                    })}
                  </SelectField>
                  <p className="mt-1.5 text-[13px] text-ink-2" data-testid="call-book-room-people">{peopleLine(r.people, groups) || 'Nobody yet'}</p>
                </li>
              ))}
            </ol>
            {ready && rooms.length > 0 && !roomsOk && <Problem line={ROOMS_SHORT} testId="call-book-rooms-short" />}
          </Section>

          <Section title="Price" testId="call-book-price">
            {price.failed ? <Problem line="Couldn’t get the price — try again." testId="call-book-price-error" />
              : price.refused ? <Problem line={price.refused} testId="call-book-price-refused" />
              : summary.length ? (
                <dl className={cn('flex flex-col gap-1.5 text-sm', price.pending && 'opacity-70')} data-testid="call-book-summary">
                  {summary.map((row, i) => (
                    <div key={i} data-row={row.kind} className={cn('flex items-baseline justify-between gap-3', (row.kind === 'people' || row.kind === 'bed') && 'pl-3 text-[13px] text-ink-2', row.kind === 'total' && 'mt-1.5 border-t border-line pt-2 font-semibold text-ink')}>
                      <dt className="min-w-0 break-words">{row.label}</dt>
                      <dd className="shrink-0 tabular-nums">{row.amount}</dd>
                    </div>
                  ))}
                </dl>
              ) : <p className="text-sm text-ink-2">{roomsOk ? 'Getting the price…' : 'The price shows once everyone has a room.'}</p>}
          </Section>
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <Section title="Guest" testId="call-book-guest">
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField label="Email" type="email" inputMode="email" autoComplete="off" maxLength={320} value={details.email ?? ''} disabled={busy}
                onChange={(e) => set('email', e.target.value)} error={shownProblems.email} data-testid="call-book-email" />
              <TextField label="Phone" type="tel" inputMode="tel" autoComplete="off" maxLength={40} value={details.phone ?? ''} disabled={busy}
                onChange={(e) => set('phone', e.target.value)} error={shownProblems.phone} data-testid="call-book-phone" />
            </div>
            {shownProblems.names && <Problem line={shownProblems.names} testId="call-book-names-problem" />}
            {shownProblems.gender && <Problem line={shownProblems.gender} testId="call-book-gender-problem" />}
            <ul className="flex flex-col gap-3">
              {slots.map((people, i) => (
                <li key={i} className="min-w-0" data-testid="call-book-names" data-room={i + 1}>
                  <p className="text-[13px] font-semibold text-ink">Room {i + 1}</p>
                  <ul className="mt-2 flex flex-col gap-3">
                    {people.map((s) => {
                      const n = nameOf(s);
                      const who2 = s.main ? `${s.label} (main guest)` : s.label;
                      const miss = Boolean(shownProblems.names);
                      return (
                        <li key={`${s.room}:${s.group}:${s.nth}`} role="group" aria-label={who2} className="min-w-0" data-testid="call-book-person" data-main={s.main || undefined}>
                          <p className="text-[13px] text-ink-2">{who2}</p>
                          <div className={cn('mt-1 grid min-w-0 gap-2', ask ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
                          <TextField label="First name" maxLength={200} autoComplete="off" value={n.first_name ?? ''} disabled={busy}
                            aria-invalid={(miss && !text(n.first_name)) || undefined} onChange={(e) => setPerson(s, { first_name: e.target.value })} data-testid="call-book-first" />
                          <TextField label="Last name" maxLength={200} autoComplete="off" value={n.last_name ?? ''} disabled={busy}
                            aria-invalid={(miss && !text(n.last_name)) || undefined} onChange={(e) => setPerson(s, { last_name: e.target.value })} data-testid="call-book-last" />
                          {ask && (
                            <SelectField label={ask === 'required' ? 'Gender' : 'Gender (optional)'} value={genderAnswer(personOf(draft.names, s).gender) ?? ''} disabled={busy}
                              onChange={(e) => setPerson(s, { gender: genderAnswer(e.target.value) ?? undefined })} data-testid="call-book-gender">
                              <option value="">{ask === 'required' ? 'Choose' : 'No answer'}</option>
                              {GENDER_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </SelectField>
                          )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
            <TextArea label="Special requests" optional rows={3} maxLength={SPECIAL_REQUESTS_MAX} value={details.special_requests ?? ''} disabled={busy}
              onChange={(e) => set('special_requests', e.target.value)} data-testid="call-book-requests" />
            {questions.map((qn) => {
              const k = customKey(qn.id);
              const v = details[k] ?? '';
              const lbl = qn.required ? qn.label : `${qn.label} (optional)`;
              if (qn.type === 'yes_no') {
                return (
                  <fieldset key={qn.id} aria-invalid={Boolean(shownProblems[k]) || undefined}>
                    <legend className="text-[13px] font-medium text-ink-2">{lbl}</legend>
                    <div className="flex gap-4">
                      {['Yes', 'No'].map((o) => (
                        <label key={o} className="inline-flex min-h-11 items-center gap-2 text-sm text-ink"><input type="radio" name={`call-book-${k}`} checked={v === o} disabled={busy} onChange={() => set(k, o)} className="size-4 accent-[var(--brand)]" />{o}</label>
                      ))}
                    </div>
                    {shownProblems[k] && <p className="text-[13px] text-st-danger">{shownProblems[k]}</p>}
                  </fieldset>
                );
              }
              return qn.type === 'long_text'
                ? <TextArea key={qn.id} label={lbl} rows={3} maxLength={2000} value={v} disabled={busy} onChange={(e) => set(k, e.target.value)} error={shownProblems[k]} />
                : <TextField key={qn.id} label={lbl} maxLength={500} value={v} disabled={busy} onChange={(e) => set(k, e.target.value)} error={shownProblems[k]} />;
            })}
            {consents.length > 0 && (
              <div className="flex flex-col gap-2" data-testid="call-book-consents">
                {consents.map((c) => (
                  <Checkbox key={c.id} label={`Guest agreed: ${c.text}`} checked={ticked.includes(c.id)} disabled={busy}
                    onChange={(e) => { setTicked(e.target.checked ? [...ticked, c.id] : ticked.filter((x) => x !== c.id)); setLine(null); }} />
                ))}
                {shownProblems.consent && <Problem line={shownProblems.consent} testId="call-book-consent-problem" />}
              </div>
            )}
          </Section>

          <Section title="For the team" testId="call-book-staff" tip="Only your team sees these. The guest never does.">
            <TextArea label="Staff note" optional rows={2} maxLength={2000} value={staffNote} disabled={busy} onChange={(e) => { setStaffNote(e.target.value); setLine(null); }} data-testid="call-book-staff-note" />
            <TextField label="How they heard about it" optional maxLength={200} autoComplete="off" value={heard} disabled={busy} onChange={(e) => { setHeard(e.target.value); setLine(null); }} data-testid="call-book-heard" />
          </Section>

          {chargeNow > 0 && (
            <Section title="Payment" testId="call-book-pay"
              tip="Card on the call charges the amount shown now. Email pay link books it unpaid: the confirmation email carries the balance and when it is due. Hold room books it unpaid and keeps the room for the hours set (the event's default unless you change it), or with no end if you tick Never expires. If nothing is paid by then, the booking is cancelled and the room goes back on sale.">
              <fieldset className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:gap-x-5">
                <legend className="sr-only">How the guest pays</legend>
                {([['card', 'Card on the call'], ['link', 'Email pay link'], ['hold', 'Hold room']] as const).map(([v, l]) => (
                  <label key={v} className="inline-flex min-h-11 items-center gap-2 text-sm text-ink">
                    <input type="radio" name="call-book-pay" value={v} checked={pay === v} disabled={busy || Boolean(locked)} onChange={() => { setPay(v); setLine(null); }} className="size-4 accent-[var(--brand)]" data-testid={`call-book-pay-${v}`} />{l}
                  </label>
                ))}
              </fieldset>
              {mode === 'hold' ? (
                <div className="flex flex-col gap-3" data-testid="call-book-hold">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-5">
                    <TextField label="Hold for (hours)" type="number" inputMode="numeric" min={1} max={HOLD_MAX_HOURS} step={1} className="sm:w-40"
                      value={holdShown} disabled={busy || Boolean(locked) || holdNever} error={holdBad ? HOLD_HOURS_BAD : null}
                      onChange={(e) => { setHoldHours(e.target.value); setLine(null); }} data-testid="call-book-hold-hours" />
                    <Checkbox label="Never expires" className="sm:mt-8" checked={holdNever} disabled={busy || Boolean(locked)}
                      onChange={(e) => { setHoldNever(e.target.checked); setLine(null); }} data-testid="call-book-hold-never" />
                  </div>
                  <p className="text-sm text-ink-2" data-testid="call-book-hold-note">Nothing is charged now. Balance {money(quote?.total_cents ?? 0, currency)}.</p>
                </div>
              ) : mode === 'link' ? (
                <p className="text-sm text-ink-2" data-testid="call-book-link-note">Nothing is charged now. Balance {money(quote?.total_cents ?? 0, currency)}.</p>
              ) : event.sandbox ? (
                <p className="text-sm text-ink-2" data-testid="call-book-practice">Practice mode: no card is needed and nothing is charged.</p>
              ) : cardUnavailable ? (
                <Problem line={CARD_NOT_SET_UP} testId="call-book-card-unavailable" />
              ) : (
                <CardForm ref={card} gateway={cardGateway!} publicKey={event.gateway_client_key!} sandbox={event.gateway_sandbox} onReady={setCardReady} unnamed />
              )}
            </Section>
          )}
          {locked && <Problem line={locked} testId="call-book-locked" report />}
          {line && <Problem line={line.text} testId="call-book-line" report={line.report} />}
        </div>
      </div>
    </>
  );
}
